import { EVisitStatus } from '@shared/api/visits/resources/anonymous/constants/visit-status'
import { EVisitType } from '@shared/api/visits/resources/anonymous/constants/visit-type'
import { EVisitType as EVisitTypes } from '@shared/api/visits/resources/admin/constants/visit-type'
import { EReporterRoleType } from '@shared/api/visits/resources/admin/constants/reporter-roles'
import { EReasonList } from '@shared/api/visits/resources/anonymous/constants/reason-list'
import { ENotificationTypes } from '@shared/api/patients/resources/notifications/constants/notification-types'
import { EDocumentType } from '@shared/api/visits/resources/documents/constants/document-type'
import { EChartObservationType } from '@shared/api/patients/resources/chart/constants/observation-type'
import { EChartObservationUnit } from '@shared/api/patients/resources/chart/constants/observation-unit'
import { EAlertLevel } from '@shared/api/patients/resources/alerts/constants/alert-level'
import { EDateFormat } from '@shared/types/constants/date-format'
import { formatDate } from '@shared/utils/date-format'

import dayjs from 'dayjs'
import { EConsentSigner } from '../api/patients/resources/patients/constants/consent-signer'

export const DEFAULT_DATE_RANGE_DATE = [
  formatDate({ date: dayjs().add(-5, 'day'), format: EDateFormat.ISO }),
  formatDate({ date: dayjs().add(1, 'day'), format: EDateFormat.ISO }),
]

export const DEFAULT_TASKS_DATE_RANGE = [
  formatDate({ date: dayjs().add(-6, 'day'), format: EDateFormat.ISO, isByTimeZone: true }),
  formatDate({ date: dayjs().add(1, 'day'), format: EDateFormat.ISO, isByTimeZone: true }),
]

export const replaceApi = /\/api/g

export const PRACTICE_ID = '00000000-0000-0000-0000-000000000000'

export const PHONE_PLACEHOLDER = '+1 (000) 000-0000'
export const PHONE_MASK_FILTER = { mask: '+1 (###) ###-####', postProcess: (value: string) => value.length == 4 ? value.replace('+1 (', '') : value }

export const DEFAULT_MASK_TOKENS = {
  '#': { pattern: /[0-9]/ },
  X: { pattern: /[0-9a-zA-Z]/ },
  S: { pattern: /[a-zA-Z]/ },
  A: { pattern: /[a-zA-Z]/, uppercase: true },
  a: { pattern: /[a-zA-Z]/, lowercase: true },
  '!': { escape: true },
  '*': { repeat: true },
}

export const SOC_NUMBER_MASK_FILTER = { mask: '###-##-####' }
export const DOB_MASK_FILTER = { mask: '##/##/####' }

export const ZIP_PLACEHOLDER = '00-000'
export const ZIP_SIMPLE_PLACEHOLDER = '00000'
export const ZIP_MASK_FILTER = { mask: '#####' }
export const DOB_PLACEHOLDER = 'MM/DD/YYYY'

export const PHONE_NUMBER_PATTERN = /^(\s*)?(\+)([- _():=+]?\d[- _():=+]?){11}(\s*)?/

export const SSN_PATTERN = /^\d{3}-\d{2}-\d{4}$/

export const ZIP_PATTERN = /^\d{5}$/

export const EMAIL_PATTERN = /^\w+([+.-]+?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/

export const DOB_PATTERN = /^(0?[1-9]|1[0-2])\/(0?[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/

export const VISIT_TYPE: { [index: string]: string } = {
  [EVisitTypes.URGENT_CARE]: 'Urgent Care',
  [EVisitTypes.URGENT_CARE_FOLLOWUP]: 'Follow-up',
  [EVisitTypes.PSYCHOLOGY]: 'Psychology',
  [EVisitTypes.WELLNESS]: 'Wellness',
  [EVisitTypes.CARE_MANAGEMENT]: 'Care Management',
  [EVisitTypes.PSYCHIATRY]: 'Psychiatry',
  [EVisitTypes.MOVEIN]: 'Move-in',
}

export const getVisitType = (visitTypeValue: EVisitType) => {
  switch (visitTypeValue) {
    case EVisitType.URGENT_CARE:
      return 'Urgent Care Visit'
    case EVisitType.URGENT_CARE_FOLLOWUP:
      return 'Follow-up'
    default:
      return 'Visit'
  }
}

export const getVisitStatus = (status: EVisitStatus) => {
  switch (status) {
    case EVisitStatus.REQUESTED:
      return 'Requested'
    case EVisitStatus.REVIEWING:
      return 'In review'
    case EVisitStatus.CANCELED:
      return 'Canceled'
    case EVisitStatus.COMPLETED:
      return 'Completed'
    case EVisitStatus.CALL_ENDED:
      return 'Call ended'
    case EVisitStatus.PENDING_TASKS:
      return 'Pending tasks'
    default:
      return 'In progress'
  }
}

export const getVisitStatusText = (status: EVisitStatus) => {
  switch (status) {
    case EVisitStatus.REQUESTED:
      return 'A Tembo clinician will be with you shortly.'
    case EVisitStatus.REVIEWING:
      return 'The clinician is reviewing your request.'
    case EVisitStatus.CALL_ENDED:
      return 'The call with clinician was ended.'
    default:
      return 'Securely connecting you to your video call.'
  }
}

export const getReporterRole = (role: string) => {
  switch (role) {
    case EReporterRoleType.COMMUNITY_STAFF:
      return 'Location staff'
    case EReporterRoleType.FAMILY:
      return 'Family'
    case EReporterRoleType.PRIVATE_CAREGIVER:
      return 'Private caregiver'
    case EReporterRoleType.RESIDENT:
      return 'Patient'
    case EReporterRoleType.COMMUNITY_CAREGIVER:
      return 'Location caregiver'
    case EReporterRoleType.COMMUNITY_NURSE:
      return 'Location nurse'
    default:
      return ''
  }
}

export const getVisitReasonText = (reason: string) => {
  switch (reason) {
    case EReasonList.ALTERED_MENTAL_STATE:
      return 'Altered mental state'
    case EReasonList.FALL:
      return 'Fall'
    case EReasonList.FOLLOWUP:
      return 'Follow-up'
    case EReasonList.FEVER:
      return 'Fever'
    case EReasonList.OTHER:
      return 'Other'
    case EReasonList.PAIN:
      return 'Pain'
    case EReasonList.RESPIRATORY_ILLNESS:
      return 'Cold / flu-like symptoms'
    case EReasonList.WOUND:
      return 'Cut / bleeding'
    case EReasonList.CONDITION_CHANGE:
      return 'Change in condition (fatigue, withdrawal, etc.)'
    case EReasonList.MEDICATION:
      return 'Medication issue'
    case EReasonList.SKIN:
      return 'Skin problem (rash, bruising, etc.)'
    case EReasonList.GASTROINTESTINAL:
      return 'Stomach issues'
    case EReasonList.URINARY:
      return 'UTI / urinary'
    case EReasonList.CONFUSION_DISORIENTATION:
      return 'Confusion or disorientation'
    case EReasonList.MEMORY_ISSUE:
      return 'Memory loss or decline'
    case EReasonList.ANXIETY_DEPRESSION:
      return 'Anxiety or depression'
    default:
      return ''
  }
}

export const notificationText = (type: string) => {
  switch (type) {
    case ENotificationTypes.CONSENT_INVALID:
      return 'Invalid consent'
    case ENotificationTypes.INSURANCE_ADDED:
      return 'Insurance added'
    case ENotificationTypes.INSURANCE_INVALID:
      return 'Invalid Insurance'
    case ENotificationTypes.NEW_RESIDENT:
      return 'New patient added'
    case ENotificationTypes.DUPLICATE_RESIDENT_WARNING:
      return 'Possible duplicated patient'
    case ENotificationTypes.CONSENT_SUBMITTED:
      return 'Consent submitted'
    case ENotificationTypes.RESIDENT_NOT_FOUND:
      return 'Patient not found'
    default:
      return ''
  }
}

export const SIGNER_LIST = [
  {
    name: 'Patient',
    value: EConsentSigner.PATIENT,
  },
  {
    name: 'Guardian or Proxy',
    value: EConsentSigner.POA,
  },
  {
    name: 'Facility Staff Member on behalf of Guardian or Proxy',
    value: EConsentSigner.STAFF,
  },
]

export const SIGNER_LIST_CLINICIAN = [
  ...SIGNER_LIST,
  {
    name: 'Clinician',
    value: EConsentSigner.CLINICIAN,
  },
]

export const getDocumentName = (item: EDocumentType, count: number | string) => {
  switch (item) {
    case EDocumentType.ENCOUNTER_SUMMARY:
      return `Encounter Summary`
    case EDocumentType.LAB_RESULT:
      return `LabResult${count}`
    case EDocumentType.SNAPSHOT:
      return `Snapshot${count}`
    case EDocumentType.IMAGE_RESULT:
      return `ImagingResult${count}`
    case EDocumentType.OTHER:
      return `Other${count}`
    default:
      return ''
  }
}

export const ALERT_LEVEL_ITEM: { [index: string]: string } = {
  [EAlertLevel.CRITICAL_HIGH]: 'Critical high',
  [EAlertLevel.CRITICAL_LOW]: 'Critical low',
  [EAlertLevel.WARNING_HIGH]: 'Warning high',
  [EAlertLevel.WARNING_LOW]: 'Warning low',
}

export const OBSERVATION_ITEM: { [index: string]: string } = {
  [EChartObservationType.BLOOD_PRESSURE_SYSTOLIC]: 'Blood pressure systolic',
  [EChartObservationType.BLOOD_PRESSURE_DIASTOLIC]: 'Blood pressure diastolic',
  [EChartObservationType.BLOOD_PRESSURE]: 'Blood pressure',
  [EChartObservationType.BLOOD_SUGAR]: 'Blood sugar',
  [EChartObservationType.BODY_HEIGHT]: 'Height',
  [EChartObservationType.BODY_TEMPERATURE]: 'Temperature',
  [EChartObservationType.BODY_WEIGHT]: 'Weight',
  [EChartObservationType.HEART_RATE]: 'Heart rate',
  [EChartObservationType.OXYGEN_SATURATION]: 'Oxygen saturation',
  [EChartObservationType.RESPIRATORY_RATE]: 'Respiratory rate',
  [EChartObservationType.SLEEP]: 'Sleep',
}

export const OBSERVATION_TAB_LIST = [
  {
    title: OBSERVATION_ITEM[EChartObservationType.BLOOD_SUGAR],
    type: EChartObservationType.BLOOD_SUGAR,
    icon: 'blood-sugar',
  },
  {
    title: OBSERVATION_ITEM[EChartObservationType.BLOOD_PRESSURE],
    type: EChartObservationType.BLOOD_PRESSURE,
    icon: 'blood-pressure',
  },

  {
    title: OBSERVATION_ITEM[EChartObservationType.BODY_TEMPERATURE],
    type: EChartObservationType.BODY_TEMPERATURE,
    icon: 'temperature',
  },
  {
    title: OBSERVATION_ITEM[EChartObservationType.OXYGEN_SATURATION],
    type: EChartObservationType.OXYGEN_SATURATION,
    icon: 'o2',
  },
  {
    title: OBSERVATION_ITEM[EChartObservationType.HEART_RATE],
    type: EChartObservationType.HEART_RATE,
    icon: 'heartbeat',
  },
  {
    title: OBSERVATION_ITEM[EChartObservationType.RESPIRATORY_RATE],
    type: EChartObservationType.RESPIRATORY_RATE,
    icon: 'respiratory-rate',
  },
  {
    title: OBSERVATION_ITEM[EChartObservationType.BODY_WEIGHT],
    type: EChartObservationType.BODY_WEIGHT,
    icon: 'weight',
  },
  {
    title: OBSERVATION_ITEM[EChartObservationType.SLEEP],
    type: EChartObservationType.SLEEP,
    icon: 'sleep',
  },
]

export const OBSERVATION_ICON: { [K in string]: string } = {
  [EChartObservationType.BLOOD_PRESSURE]: 'blood-pressure',
  [EChartObservationType.BLOOD_SUGAR]: 'blood-sugar',
  [EChartObservationType.BODY_HEIGHT]: 'height',
  [EChartObservationType.BODY_TEMPERATURE]: 'temperature',
  [EChartObservationType.BODY_WEIGHT]: 'weight',
  [EChartObservationType.HEART_RATE]: 'heartbeat',
  [EChartObservationType.OXYGEN_SATURATION]: 'o2',
  [EChartObservationType.RESPIRATORY_RATE]: 'respiratory-rate',
  [EChartObservationType.SLEEP]: 'sleep',
}

export const OBSERVATION_UNIT_LABEL: { [K in string]: string } = {
  [EChartObservationUnit.BREATHS_PER_MIN]: 'breaths per min (BPM)',
  [EChartObservationUnit.CENTIMETER]: 'cm',
  [EChartObservationUnit.DEGREES_CELSIUS]: '°C',
  [EChartObservationUnit.DEGREES_FAHRENHEIT]: '°F',
  [EChartObservationUnit.HEARTBEATS_PER_MIN]: 'beats per min (bpm)',
  [EChartObservationUnit.INCH]: 'in.',
  [EChartObservationUnit.KILOGRAM]: 'kg',
  [EChartObservationUnit.MILLIMETERS_OF_MERCURY]: 'mmHg',
  [EChartObservationUnit.MILLIGRAMS_PER_DECILITER]: 'mg/dL',
  [EChartObservationUnit.MILLIMOLES_PER_LITER]: 'mmol/L',
  [EChartObservationUnit.PERCENT]: '%',
  [EChartObservationUnit.POUND]: 'lbs.',
  [EChartObservationUnit.MINUTES]: 'ml',
}

export const OBSERVATION_UNIT_LABEL_MOBILE: { [K in string]: string } = {
  [EChartObservationUnit.BREATHS_PER_MIN]: 'BPM',
  [EChartObservationUnit.CENTIMETER]: 'cm',
  [EChartObservationUnit.DEGREES_CELSIUS]: '°C',
  [EChartObservationUnit.DEGREES_FAHRENHEIT]: '°F',
  [EChartObservationUnit.HEARTBEATS_PER_MIN]: 'bpm',
  [EChartObservationUnit.INCH]: 'in.',
  [EChartObservationUnit.KILOGRAM]: 'kg',
  [EChartObservationUnit.MILLIMETERS_OF_MERCURY]: 'mmHg',
  [EChartObservationUnit.MILLIGRAMS_PER_DECILITER]: 'mg/dL',
  [EChartObservationUnit.MILLIMOLES_PER_LITER]: 'mmol/L',
  [EChartObservationUnit.PERCENT]: '%',
  [EChartObservationUnit.POUND]: 'lbs.',
}

export const PAGINATION_STEP = 8

export const CONSENT_SUMMARY_ORDER = {
  consented: 0,
  invalid: 1,
  emailed: 2,
  submitted: 3,
  declined: 4,
  none: 5,
}

export const INSURANCE_SUMMARY_ORDER = {
  verified: 0,
  submitted: 1,
  declined: 2,
  invalid: 3,
  none: 4,
}

export const OPT_IN_SUMMARY_ORDER = {
  enrolled: 0,
  declined: 1,
  none: 2,
}
